import { describeApiError } from '@brush/api-client';
import { Award, DollarSign, Flame, Sparkles, Trophy } from 'lucide-react';
import type { PortalView } from '../nav';
import { usePlayer } from '../player';
import { useScreenViewed } from '../api';
import { HOME_RECENT, NOT_YOURS, dollars, entriesHint, figureText, headerCounts, placeLabel, readState, runningCount } from '../ownResults';
import { useFantasyContests, useLeaderboards, useOwnResults } from '../queries';

export function HomeView({ onNavigate }: { onNavigate: (view: PortalView) => void }) {
  const { player, room, theme, readOnly } = usePlayer();
  useScreenViewed('home', player?.roomId ?? null);
  const roomId = room?.id ?? null;
  const resultsQ = useOwnResults(roomId, 1, HOME_RECENT, !readOnly);
  const leaderboardsQ = useLeaderboards(roomId);
  const contestsQ = useFantasyContests(roomId);

  if (!player) return null;

  // Each read is one of four states, and none of them is "nothing" until it has answered (known gap 93):
  // a figure reads … while it is on its way and — when it failed, never 0.
  const failure = (q: { isError: boolean; error: unknown }) => (q.isError ? describeApiError(q.error) : null);
  const results = readState({ readOnly, isPending: resultsQ.isPending, failure: failure(resultsQ), data: resultsQ.data });
  const boards = readState({ readOnly: false, isPending: leaderboardsQ.isPending, failure: failure(leaderboardsQ), data: leaderboardsQ.data });
  const contests = readState({ readOnly: false, isPending: contestsQ.isPending, failure: failure(contestsQ), data: contestsQ.data });
  const openContests = contests.kind === 'ready' ? contests.data.filter((c) => c.status === 'open') : [];

  const card = {
    background: theme.panel,
    border: `1px solid ${theme.border}`,
  };

  return (
    <div className="space-y-6">
      <header
        style={{
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.bg} 100%)`,
          border: `1px solid ${theme.border}`,
        }}
        className="relative overflow-hidden rounded-2xl p-8"
      >
        <div className="pointer-events-none absolute right-0 top-0 text-[200px] font-bold leading-none opacity-5">
          {room?.logo}
        </div>
        <div className="relative">
          <p className="mb-2 text-sm font-semibold uppercase tracking-widest" style={{ color: theme.accent }}>
            Welcome back
          </p>
          <h1 className="mb-2 text-4xl font-bold">{player.name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: theme.textMuted }}>
            <span className="flex items-center gap-1">
              <Trophy className="size-4" style={{ color: theme.accent }} /> {headerCounts(player).wins}
            </span>
            <span className="flex items-center gap-1">
              <Award className="size-4" style={{ color: theme.accent }} /> {headerCounts(player).cashes}
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="size-4" style={{ color: theme.accent }} /> $
              {player.totalWinnings.toLocaleString()}
            </span>
          </div>
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        {room?.canLeaderboard ? (
          <button type="button" onClick={() => onNavigate('leaderboards')} style={card} className="rounded-xl p-5 text-left transition hover:opacity-90">
            <div className="mb-3 flex items-center justify-between">
              <Trophy className="size-5" style={{ color: theme.accent }} />
              <span className="text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>
                Leaderboards
              </span>
            </div>
            <div className="mb-1 text-3xl font-bold">{figureText(boards, runningCount)}</div>
            <div className="text-sm" style={{ color: theme.textMuted }}>
              Running at {room.name}
            </div>
          </button>
        ) : null}

        {room?.canFantasy ? (
          <button type="button" onClick={() => onNavigate('fantasy')} style={card} className="rounded-xl p-5 text-left transition hover:opacity-90">
            <div className="mb-3 flex items-center justify-between">
              <Sparkles className="size-5" style={{ color: theme.accent }} />
              <span className="text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>
                Fantasy open
              </span>
            </div>
            <div className="mb-1 text-3xl font-bold">{figureText(contests, (all) => all.filter((c) => c.status === 'open').length)}</div>
            <div className="text-sm" style={{ color: theme.textMuted }}>
              Contests accepting entries
            </div>
          </button>
        ) : null}

        <button type="button" onClick={() => onNavigate('stats')} style={card} className="rounded-xl p-5 text-left transition hover:opacity-90">
          <div className="mb-3 flex items-center justify-between">
            <Flame className="size-5" style={{ color: theme.accent }} />
            <span className="text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>
              Your results
            </span>
          </div>
          <div className="mb-1 text-3xl font-bold">{figureText(results, (r) => r.totals.entries)}</div>
          <div className="text-sm" style={{ color: theme.textMuted }}>
            Entries{results.kind === 'ready' ? ` · ${entriesHint(results.data.totals)}` : ''}
          </div>
        </button>
      </div>

      <section style={card} className="rounded-xl p-6">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
          Your recent results
        </h2>
        {results.kind === 'loading' ? (
          <p className="py-8 text-center text-sm" style={{ color: theme.textMuted }}>
            Loading your results…
          </p>
        ) : results.kind === 'failed' ? (
          <p className="py-8 text-center text-sm" style={{ color: theme.textMuted }}>
            Your results couldn't be loaded. {results.message}
          </p>
        ) : results.kind === 'notYours' ? (
          <p className="py-8 text-center text-sm" style={{ color: theme.textMuted }}>
            {NOT_YOURS}
          </p>
        ) : results.data.data.length === 0 ? (
          <p className="py-8 text-center text-sm" style={{ color: theme.textMuted }}>
            No results recorded yet. They appear here once your room uploads tournament results.
          </p>
        ) : (
          <ul className="space-y-2">
            {results.data.data.map((r) => (
              <li
                key={r.tournamentId}
                style={{ background: theme.panelLight }}
                className="flex items-center justify-between rounded-lg p-3"
              >
                <div className="flex items-center gap-4">
                  <span
                    className="flex size-10 items-center justify-center rounded-full text-sm font-bold"
                    style={{
                      background: r.position === 1 ? theme.accent : theme.border,
                      color: r.position === 1 ? theme.bg : theme.text,
                    }}
                  >
                    {placeLabel(r.position)}
                  </span>
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-xs" style={{ color: theme.textMuted }}>
                      {r.date}
                    </div>
                  </div>
                </div>
                <div className="font-bold" style={{ color: theme.accent }}>
                  {dollars(r.prize)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {room?.canFantasy && openContests.length > 0 ? (
        <section style={card} className="rounded-xl p-6">
          <h2 className="mb-4 text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
            Open fantasy contests
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {openContests.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onNavigate('fantasy')}
                style={{ background: theme.panelLight, border: `1px solid ${theme.border}` }}
                className="rounded-lg p-4 text-left transition hover:opacity-90"
              >
                <div className="mb-2 flex items-start justify-between">
                  <h3 className="font-semibold">{c.name}</h3>
                  <Sparkles className="size-4" style={{ color: theme.accent }} />
                </div>
                <div className="space-y-1 text-xs" style={{ color: theme.textMuted }}>
                  <div>
                    ${c.budget.toLocaleString()} budget · {c.rosterSize} picks
                  </div>
                  <div>{c.entries} entered</div>
                </div>
                <div className="mt-3 text-sm font-semibold uppercase tracking-wider" style={{ color: theme.accent }}>
                  Build your team →
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
